import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router";
import { FaCodeBranch, FaPlus, FaCheck, FaStar } from "react-icons/fa";
import { useBranches } from "@/api/branch";
import { useBranchStore } from "@/hooks/use-branch-store";
import { Branch } from "@/models/branch";
import { useWorkspaceTheme } from "@/hooks/use-workspace-theme";
import CreateBranchModal from "@/components/branches/CreateBranchModal";

/**
 * Branches section for the Arc sidebar.
 * Displays all branches when on the /branches route.
 * Allows switching between branches and creating new ones.
 */
const BranchesSection: React.FC = () => {
  const navigate = useNavigate();
  const { mode } = useWorkspaceTheme();
  const { data: branches = [], refetch } = useBranches();
  const { activeBranchId, setActiveBranch } = useBranchStore();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch branches on mount
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Sort branches: main first, then by name
  const sortedBranches = useMemo(() => {
    return [...branches].sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [branches]);

  // Handle branch selection
  const handleSelectBranch = useCallback(
    (branch: Branch) => {
      // null = main branch (no header sent)
      const branchId = branch.is_default ? null : branch.id;
      setActiveBranch(branchId);
    },
    [setActiveBranch]
  );

  // Check if a branch is active
  const isBranchActive = useCallback(
    (branch: Branch) => {
      if (branch.is_default) {
        return activeBranchId === null;
      }
      return activeBranchId === branch.id;
    },
    [activeBranchId]
  );

  // Get the active branch name for display
  const activeBranchName = useMemo(() => {
    if (activeBranchId === null) return "main";
    const branch = branches.find((b) => b.id === activeBranchId);
    return branch?.name || "main";
  }, [activeBranchId, branches]);

  // Handle create branch success
  const handleBranchCreated = useCallback(() => {
    refetch();
    setIsCreateModalOpen(false);
  }, [refetch]);

  if (branches.length === 0) {
    return (
      <div className="px-3 py-2">
        {/* Section Title */}
        <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
          <FaCodeBranch size={10} />
          <span className="text-[10px] font-semibold uppercase tracking-wider">
            Branches
          </span>
          <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
        </div>

        {/* Loading/Empty state */}
        <div className="px-2 py-4 text-center">
          <p className="text-[var(--arc-sidebar-text-secondary)] text-xs">
            Chargement...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      {/* Section Title */}
      <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
        <FaCodeBranch size={10} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          Branches
        </span>
        <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
      </div>

      {/* Current Branch Indicator */}
      <div className="px-2 mb-3">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-[var(--arc-sidebar-accent-glow)]">
          <FaCodeBranch
            size={12}
            className="text-[var(--arc-accent)]"
          />
          <span className="text-xs font-medium text-[var(--arc-sidebar-text-primary)]">
            {activeBranchName}
          </span>
          {activeBranchId === null && (
            <FaStar
              size={8}
              className="text-yellow-500 ml-auto"
              title="Default branch"
            />
          )}
        </div>
      </div>

      {/* Branches List */}
      <div className="space-y-0.5">
        {sortedBranches.map((branch) => {
          const isActive = isBranchActive(branch);
          return (
            <button
              key={branch.id}
              onClick={() => handleSelectBranch(branch)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-all ${
                isActive
                  ? "bg-[var(--arc-sidebar-accent-glow)] text-[var(--arc-sidebar-text-primary)]"
                  : "text-[var(--arc-sidebar-text-secondary)] hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)]"
              }`}
            >
              <FaCodeBranch
                size={10}
                className={isActive ? "text-[var(--arc-accent)]" : ""}
              />
              <span className="text-xs truncate flex-1">{branch.name}</span>
              {branch.is_default && (
                <FaStar
                  size={8}
                  className="text-yellow-500 opacity-60"
                  title="Default branch"
                />
              )}
              {isActive && (
                <FaCheck
                  size={8}
                  className="text-[var(--arc-accent)]"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* New Branch Button */}
      <div className="mt-3 px-2">
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[var(--arc-sidebar-border-accent)] text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)] hover:border-[var(--arc-accent)] hover:bg-[var(--arc-sidebar-accent-glow)] transition-all text-xs"
        >
          <FaPlus size={10} />
          <span>New Branch</span>
        </button>
      </div>

      {/* Create Branch Modal */}
      <CreateBranchModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleBranchCreated}
      />
    </div>
  );
};

BranchesSection.displayName = "BranchesSection";

export default BranchesSection;
